import React, { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import { Element, scrollSpy } from 'react-scroll';
import { useScroll, useMotionValueEvent } from 'framer-motion';
import { MapPinIcon } from '@heroicons/react/20/solid';

import OwnerHeader from '../../components/Listings/Headers/OwnerHeader';
import ListingAdminHeader from '../../components/Listings/Headers/Admin';
import { ListingSEOTitle, ListingTitle } from '../../components/Listings/Sections/Title';
import { ListingRating, ListingReviews } from '../../components/Listings/Sections/Reviews';
import ListingBreadcrumbs from '../../components/Listings/Sections/Breadcrumbs';
import ListingPhotoMosaic from '../../components/Listings/Sections/PhotoMosaic';
import { ListingBadge } from '../../components/Listings/Badge';
import { DescriptionIcons } from '../../components/Listings/Sections/DescriptionIcons';
import { ListingIdealUses } from '../../components/Listings/Sections/IdealUses';
import { ListingDescription } from '../../components/Listings/Sections/Description';
import { ListingAmenities } from '../../components/Listings/Sections/Amenities';
import { ListingPhotosList } from '../../components/Listings/Sections/PhotoList';
import { ListingOperatingHours } from '../../components/Listings/Sections/OperatingHours';
import { ListingFAQ } from '../../components/Listings/Sections/FAQ';
import { ListingAvailabilityCalendar } from '../../components/Listings/Sections/AvailibityCalendar';
import { ListingRightPanel } from '../../components/Listings/Sections/RightPanel';
import { ListingStickyNavbar } from '../../components/Listings/Sections/Navbar';

const ListingMap = lazy(() => import( /* webpackChunkName: "listing-map" */ '../../components/Listings/Sections/Map'));

export const SCOPED_REDUCERS = ['listing'];

export function RootComponent() {
  const { _meta, reviews, is_hourly_enabled, operating_hours, id } = useSelector((state) => state.listing);
  const [showHeader, setShowHeader] = useState(false);

  const listingContainerRef = useRef(null);
  const { scrollY, scrollYProgress } = useScroll({ target: listingContainerRef });

  const descriptionIconsRef = useRef(null);
  const idealUsesRef = useRef(null);
  const descriptionRef = useRef(null);
  const amenitiesRef = useRef(null);
  const reviewsRef = useRef(null);
  const photosRef = useRef(null);
  const calendarRef = useRef(null);
  const locationRef = useRef(null);

  const [isBrowser, setIsBrowser] = useState(false);

  useEffect(() => {
    setIsBrowser(true);
  }, []);

  const { ref: listingMapRef, inView: listingMapInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [scrollProgress, setScrollProgress] = useState({});

  useMotionValueEvent(scrollY, 'change', (latest) => {
    if (listingContainerRef && listingContainerRef.current) {
      let progress = {};
      const listingDetailsEl = listingContainerRef.current.querySelector('.listing-details');
      const offsetTop = listingDetailsEl.offsetTop - 64;

      [
        descriptionIconsRef, idealUsesRef, descriptionRef, amenitiesRef, reviewsRef, photosRef, calendarRef, locationRef
      ].forEach(({ current: element }) => {
        const bottom = Math.max(latest, (element.offsetTop + offsetTop)) - (element.offsetTop + offsetTop);
        const top = element.offsetTop + offsetTop + element.scrollHeight - (element.offsetTop + offsetTop);

        progress = ({
          ...progress,
          [element.dataset.refName]: Math.min(1, bottom/top)
        });
      });

      setScrollProgress({ ...progress });
    }
  });

  useEffect(() => {
    setShowHeader(
      (scrollProgress.icons && scrollProgress.icons > 0)
      && (!scrollProgress.location || (scrollProgress && scrollProgress.location < 1))
    );
  }, [scrollProgress]);

  useEffect(() => {
    // Updating scrollSpy when the component mounts.
    scrollSpy.update();
  }, []);

  return (
    <div ref={listingContainerRef}>
      {
        _meta.current_user.is_admin
          ? <ListingAdminHeader/>
          : (_meta.current_user.is_owner && <OwnerHeader/>)
      }

      <div className="mt-8 mb-6 hidden md:block">
        <ListingTitle />
        <div className="flex items-center mt-1 justify-between">
          <div className="flex">
            {reviews.length > 0 && (
              <>
                <ListingRating />
                <div className="inline-block font-medium pt-1 leading-3 mx-1.5 text-stone-400">
                  &middot;
                </div>
              </>
            )}
            <ListingBreadcrumbs />
          </div>
          <div
            className="favorite-tag"
            data-ng-cloak
            data-sf-favorite-btn
            data-listing-id={id}
            data-listing="listing"
            data-separate-buttons="true"
            data-event-tracking-type="'page'"
            data-event-tracking-on-favorite="true"/>
        </div>
      </div>

      <ListingPhotoMosaic/>

      <div className="listing-details">
        {/* Inquiry panel */}
        <ListingRightPanel/>

        {/* Content panel */}
        <div className="listing-details-left pt-4">
          {/* Listing sticky navbar */}
          <ListingStickyNavbar show={showHeader} scrollProgress={scrollProgress}/>

          <div className="max-w-6xl w-full mb-2">
            <div className="block px-4 md:hidden md:px-0 text-center md:text-left">
              <ListingTitle/>

              <div className="flex flex-col flex-wrap items-center md:flex-row md:items-center my-1">
                <div className="listing-badge inline-block md:hidden">
                  <ListingBadge/>
                </div>
                <div className="hidden font-medium leading-3 ml-1.5 mr-1 text-stone-400 text-xl md:block">
                  &middot;
                </div>
                <div className="items-center text-stone-700 flex mt-2 md:mt-0 md:hidden">
                  <MapPinIcon width={16} />
                  <a href={_meta.searchUrl} target="_blank" className="text-stone-900 font-normal underline ml-1 underline-offset-2 decoration-1" rel="noreferrer">
                    {_meta.locationName}
                  </a>
                </div>
              </div>

              <div className="flex items-center flex-wrap justify-center">
                {reviews.length > 0 && (
                  <div className="flex items-center">
                    <ListingRating/>
                    <div className="inline-block font-medium pt-1 leading-3 mx-1.5 text-stone-400 hidden md:block">
                      &middot;
                    </div>
                  </div>
                )}
                <div className="hidden md:block">
                  <ListingBreadcrumbs/>
                </div>
              </div>
            </div>

            <hr className="listing-details-separator block md:hidden"/>

            <div className="px-4 md:px-0">
              <ListingSEOTitle/>
              <div className="flex flex-wrap items-center mb-2 md:mt-2">
                <div className="listing-badge mr-2 sm:mr-0 hidden md:block">
                  <ListingBadge/>
                </div>
                <div className="inline-block hidden font-medium leading-3 ml-1.5 mr-1 text-stone-400 text-xl md:block">
                  &middot;
                </div>

                <div className="items-center text-stone-700 hidden md:flex">
                  <MapPinIcon width={16} />
                  <a href={_meta.searchUrl} target="_blank" className="text-stone-900 font-light underline ml-1 underline-offset-2 decoration-1" rel="noreferrer">
                    {_meta.locationName}
                  </a>
                </div>
                <div className="block md:hidden">
                  <ListingBreadcrumbs/>
                </div>
              </div>
            </div>

            <div ref={descriptionIconsRef} data-ref-name="icons">
              <hr className="listing-details-separator"/>
              <DescriptionIcons/>
            </div>

            <Element name="ideal_uses">
              <div ref={idealUsesRef} data-ref-name="uses">
                <hr className="listing-details-separator"/>
                <ListingIdealUses/>
              </div>
            </Element>

            <Element name="description">
              <div ref={descriptionRef} data-ref-name="description">
                <hr className="listing-details-separator"/>
                <ListingDescription/>
              </div>
            </Element>

            <Element name="amenities">
              <div ref={amenitiesRef} data-ref-name="amenities">
                <hr className="listing-details-separator"/>
                <ListingAmenities/>
              </div>
            </Element>

            <Element name="reviews">
              <div ref={reviewsRef} data-ref-name="reviews">
                <hr className="listing-details-separator"/>
                <ListingReviews/>
              </div>
            </Element>

            <Element name="photos">
              <div ref={photosRef} data-ref-name="photos">
                <hr className="listing-details-separator"/>
                <ListingPhotosList/>
              </div>
            </Element>

            <Element name="availabilities">
              <div ref={calendarRef} data-ref-name="availabilities">
                {
                  is_hourly_enabled && operating_hours.length > 0 && (
                    <>
                      <hr className="listing-details-separator"/>
                      <ListingOperatingHours/>
                    </>
                  )
                }
                <hr className="listing-details-separator"/>
                <ListingAvailabilityCalendar/>
              </div>
            </Element>

            <Element name="location">
              <div ref={locationRef} data-ref-name="location">
                <hr ref={listingMapRef} className="listing-details-separator"/>
                {isBrowser && listingMapInView && (
                  <Suspense fallback={<div style={{height: '384px'}} />}>
                    <ListingMap/>
                  </Suspense>
                )}
                <ListingFAQ/>
              </div>
            </Element>
          </div>
        </div>
      </div>
    </div>
  );
}
